import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServerConfigService } from '@app/configuration/server-config.service';
import { plainToClass } from 'class-transformer';
import { EMPTY, Observable } from 'rxjs';
import {
  catchError, map, share, tap,
} from 'rxjs/operators';
import { StateService } from '../state/state.service';
import { CreateOrder } from './i-create-order';
import { IOrderAction } from './i-order-action';
import { IOrderFormChange } from './i-order-form';
import { Order } from './order';
import { OrderActions } from './order-actions.enum';
import { OrderState } from './order-state';
import { PersistedOrder } from './persisted-order';

@Injectable({ providedIn: 'root' })
export class OrdersService {
  private _state: OrderState;

  constructor(
    private stateService: StateService,
    private http: HttpClient,
    private serverConfigService: ServerConfigService,
  ) {
    this.state$.subscribe((x) => { this._state = x; });
  }

  get state(): OrderState {
    return this._state;
  }

  get state$(): Observable<OrderState> {
    return this.stateService.selectDistinct('orders');
  }

  updateList = () => {
    this.stateService.dispatch({ type: OrderActions.UPDATE_LIST_REQUEST });
    this.http
      .get(`${this.serverConfigService.config.apiBaseUrl}/orders`)
      .pipe(
        catchError((error) => {
          this.stateService.dispatch({
            type: OrderActions.UPDATE_LIST_ERROR,
            error,
          });

          return error;
        }),
      )
      .subscribe((result) => {
        const orders = plainToClass(Order, result);
        if (!Array.isArray(orders)) throw new Error('Only one order instead of array!');
        this.stateService.dispatch({
          type: OrderActions.UPDATE_LIST_SUCCESS,
          orders,
        });
      });
  };

  dispatch(action: IOrderAction) {
    this.stateService.dispatch(action);
  }

  formChanges(val: IOrderFormChange, changeIndex: number): any {
    this.dispatch({
      type: OrderActions.FORM_CHANGE,
      formValues: { ...val, changeIndex },
    });
  }

  createOrder = (order: CreateOrder) => {
    const result$ = this.http
      .post(`${this.serverConfigService.config.apiBaseUrl}/orders`, order)
      .pipe(
        share(),
        tap(() => this.stateService.dispatch({
          type: OrderActions.CREATE_REQUEST,
        })),
        catchError((error) => {
          this.stateService.dispatch({
            type: OrderActions.CREATE_ERROR,
            error,
          });

          return EMPTY;
        }),
        map((result) => plainToClass(PersistedOrder, result)),
      );
    result$.subscribe((persistedOrder) => {
      this.stateService.dispatch({
        type: OrderActions.CREATE_SUCCESS,
        order: persistedOrder,
      });
    });

    return result$;
  };

  updateOrder = (orderId: string): Observable<PersistedOrder> => {
    this.stateService.dispatch({ type: OrderActions.UPDATE_REQUEST });
    const order$ = this.http
      .get<Order>(
      `${this.serverConfigService.config.apiBaseUrl}/orders/${orderId}`,
    )
      .pipe(
        catchError((error) => {
          this.stateService.dispatch({
            type: OrderActions.UPDATE_ERROR,
            error,
          });

          return EMPTY;
        }),
        map((response) => plainToClass(PersistedOrder, response)),
      );

    order$.subscribe((order) => {
      this.stateService.dispatch({
        type: OrderActions.UPDATE_SUCCESS,
        order,
      });
    });

    return order$;
  };

  getOrderSubscription = (orderId: string) => this.http.get(
    `${this.serverConfigService.config.apiBaseUrl}/orders/${orderId}`,
  );

  getOrder = (orderId: string) => {
    this.stateService.dispatch({ type: OrderActions.UPDATE_REQUEST });
    this.getOrderSubscription(orderId)
      .pipe(
        catchError((error) => {
          this.stateService.dispatch({
            type: OrderActions.UPDATE_ERROR,
            error,
          });

          return error;
        }),
      )
      .subscribe((result) => {
        const order = plainToClass(Order, result);
        this.stateService.dispatch({
          type: OrderActions.UPDATE_SUCCESS,
          order,
        });
      });
  };
}
